/* =============================================================================
   Delivery destination drawer. PeekAPaw customisation.

   The destination is a postcode plus a suburb, because peek-dream needs both to
   resolve a shipping zone. The drawer therefore looks the suburbs up from the
   postcode and makes the customer pick one.

   Everything this file stores goes through window.deliveryLocation.applyState
   (defined in snippets/delivery-location-head.liquid), which is what actually
   swaps the visible copy. Nothing here writes into the product page directly.

   Pairs with snippets/delivery-location.liquid and assets/delivery-location.css.
   ========================================================================== */

(function () {
  var config = window.deliveryLocation;

  if (!config) {
    return;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // Shopify mis-detects storefront POSTs to the app proxy without this.
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Delivery lookup failed with ' + response.status);
      }

      return response.json();
    }).then(function (payload) {
      if (!payload || payload.code !== 200) {
        throw new Error((payload && payload.error) || 'Delivery lookup was rejected');
      }

      return payload.data;
    });
  }

  function resolveZone(province, postcode, suburb) {
    return postJSON(config.zoneUrl, {
      state: province,
      postcode: postcode,
      suburb: suburb
    }).then(function (zone) {
      var freeThreshold = null;

      if (zone && Array.isArray(zone.rules)) {
        zone.rules.forEach(function (rule) {
          if (Number(rule.fee) === 0) {
            var threshold = Number(rule.min_subtotal) / 100;

            if (freeThreshold === null || threshold < freeThreshold) {
              freeThreshold = threshold;
            }
          }
        });
      }

      return {
        postcode: postcode,
        suburb: suburb,
        country: 'Australia',
        province: (zone && zone.state) || province,
        eligible: freeThreshold !== null,
        threshold: freeThreshold,
        rules: zone && Array.isArray(zone.rules) ? zone.rules : [],
        checkedAt: Date.now()
      };
    });
  }

  function saveState(state) {
    try {
      localStorage.setItem(config.storageKey, JSON.stringify(state));
    } catch (error) {
      // A full or blocked localStorage only costs us the cache, not the feature.
    }

    config.applyState(state);
    document.dispatchEvent(new CustomEvent('delivery-location:changed', {
      detail: { state: state }
    }));
  }

  function provinceForPostcode(postcode) {
    var value = parseInt(postcode, 10);

    if ((value >= 200 && value <= 299) || (value >= 2600 && value <= 2618) || (value >= 2900 && value <= 2920)) {
      return 'ACT';
    }
    if ((value >= 1000 && value <= 2599) || (value >= 2619 && value <= 2899) || (value >= 2921 && value <= 2999)) {
      return 'NSW';
    }
    if ((value >= 800 && value <= 999)) {
      return 'NT';
    }
    if ((value >= 3000 && value <= 3999) || (value >= 8000 && value <= 8999)) {
      return 'VIC';
    }
    if ((value >= 4000 && value <= 4999) || (value >= 9000 && value <= 9999)) {
      return 'QLD';
    }
    if (value >= 5000 && value <= 5999) {
      return 'SA';
    }
    if (value >= 6000 && value <= 6999) {
      return 'WA';
    }
    if (value >= 7000 && value <= 7999) {
      return 'TAS';
    }

    return '';
  }

  /* --- keep the stored zone fresh ----------------------------------------- */

  function refreshStoredZone() {
    var state = config.readState() || config.customerAddress;

    if (!state || !state.postcode || !state.suburb) {
      return;
    }

    if (state.checkedAt && Date.now() - state.checkedAt < config.ttl && Array.isArray(state.rules)) {
      return;
    }

    resolveZone(
      state.province || provinceForPostcode(state.postcode),
      state.postcode,
      state.suburb
    ).then(saveState).catch(function () {
      // Leave whatever is cached in place; the bar simply will not update.
    });
  }

  /* --- drawer -------------------------------------------------------------- */

  function initDrawer() {
    var form = document.querySelector('[data-delivery-location-form]');

    if (!form) {
      return;
    }

    var queryInput = form.querySelector('[data-delivery-location-query]');
    var results = form.querySelector('[data-delivery-location-results]');
    var feedback = form.querySelector('[data-delivery-location-feedback]');
    var triggers = document.querySelectorAll('[aria-controls="modal-delivery-location"]');
    var modal = form.closest('.modal');
    var pendingQuery = '';
    var lookupTimer = null;

    function setFeedback(message, isError) {
      feedback.textContent = message || '';
      feedback.hidden = !message;
      feedback.classList.toggle('delivery-location__feedback--error', Boolean(isError));
    }

    function setResults(locations) {
      results.innerHTML = '';

      locations.forEach(function (location) {
        var button = document.createElement('button');
        var postcode = document.createElement('strong');
        var address = document.createElement('span');

        button.type = 'button';
        button.className = 'delivery-location__result';
        button.setAttribute('role', 'option');
        button.dataset.postcode = location.postcode || '';
        button.dataset.suburb = location.suburb || '';
        button.dataset.state = location.state || '';

        postcode.textContent = location.postcode || '';
        address.textContent = [
          location.suburb,
          location.state,
          'Australia'
        ].filter(Boolean).join(' ');

        button.appendChild(postcode);
        button.appendChild(address);
        results.appendChild(button);
      });

      results.hidden = locations.length === 0;
    }

    function searchLocations(force) {
      var query = queryInput.value.trim();

      if (query.length < 3) {
        pendingQuery = '';
        setResults([]);
        setFeedback('');
        return;
      }

      if (!force && query === pendingQuery) {
        return;
      }

      pendingQuery = query;
      setResults([]);
      setFeedback(form.dataset.searchingMessage);

      postJSON(config.postcodeSearchUrl, { q: query }).then(function (locations) {
        if (query !== pendingQuery) {
          return;
        }

        locations = (locations || []).filter(function (location) {
          return location.postcode && location.suburb && location.state;
        });

        if (!locations.length) {
          setResults([]);
          setFeedback(form.dataset.noResultsMessage);
          return;
        }

        setResults(locations);
        setFeedback('');
      }).catch(function () {
        if (query !== pendingQuery) {
          return;
        }

        pendingQuery = '';
        setResults([]);
        setFeedback(form.dataset.searchErrorMessage, true);
      });
    }

    function prefill() {
      var state = config.readState() || config.customerAddress;

      pendingQuery = '';
      clearTimeout(lookupTimer);
      setResults([]);
      setFeedback('');

      if (state && state.postcode) {
        queryInput.value = state.postcode;
        searchLocations(true);
      } else {
        queryInput.value = '';
      }
    }

    queryInput.addEventListener('input', function () {
      clearTimeout(lookupTimer);
      lookupTimer = setTimeout(function () {
        searchLocations(false);
      }, 250);
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearTimeout(lookupTimer);
      searchLocations(true);
    });

    results.addEventListener('click', function (event) {
      var result = event.target.closest('[data-postcode]');

      if (!result || result.disabled) {
        return;
      }

      var buttons = results.querySelectorAll('button');
      buttons.forEach(function (button) {
        button.disabled = true;
      });
      setFeedback(form.dataset.savingMessage);

      resolveZone(
        result.dataset.state,
        result.dataset.postcode,
        result.dataset.suburb
      ).then(function (state) {
        saveState(state);
        document.dispatchEvent(new CustomEvent('modal:close'));
      }).catch(function () {
        setFeedback(form.dataset.saveErrorMessage, true);
      }).then(function () {
        buttons.forEach(function (button) {
          button.disabled = false;
        });
      });
    });

    // ModalManager has no "opened" event, so the drawer is filled from the
    // same click that opens it. These listeners sit on the triggers themselves
    // and so run before the body-level delegate that stops propagation.
    Array.prototype.forEach.call(triggers, function (trigger) {
      trigger.addEventListener('click', prefill);
    });

    if (modal) {
      modal.addEventListener('modal:closed', function () {
        setFeedback('');
      });
    }
  }

  /* --- shipping estimate --------------------------------------------------- */

  function selectOption(select, wanted) {
    if (!select || !wanted) {
      return false;
    }

    var normalized = String(wanted).toUpperCase();

    for (var i = 0; i < select.options.length; i++) {
      var option = select.options[i];

      if (option.value.toUpperCase() === normalized || option.text.toUpperCase() === normalized) {
        select.selectedIndex = i;
        return true;
      }
    }

    return false;
  }

  /*
    Drives the theme's ShippingEstimator from the saved destination instead of
    from the address form it ships with: the fields are filled off-screen and
    the Estimate button is clicked for the customer.

    The cart quotes on its own (`auto`), since its contents are already decided.
    The product page waits for the customer to ask, and calls `invalidate` from
    `bind` whenever the quote stops describing what is on screen.
  */
  function initShippingEstimate(settings) {
    var lastEstimate = {
      key: '',
      time: 0
    };
    var estimateTimer = null;
    var estimatingTimer = null;

    function setEstimating(on) {
      var card = document.querySelector(settings.cardSelector);

      if (!card) {
        return;
      }

      card.classList.toggle('is-estimating', on);

      // Styling the trigger as busy still leaves it reachable by keyboard, so
      // the double-submit guard has to be the real disabled state.
      var trigger = card.querySelector('[data-action="refresh-shipping"]');

      if (trigger) {
        trigger.disabled = on;
        trigger.setAttribute('aria-busy', on ? 'true' : 'false');
      }

      clearTimeout(estimatingTimer);

      // theme:loading:end is what normally releases the trigger. A request that
      // never settles would otherwise leave it disabled for the whole session.
      if (on) {
        estimatingTimer = setTimeout(function () {
          setEstimating(false);
        }, 15000);
      }
    }

    function estimate(state, force) {
      state = state || config.readState() || config.customerAddress;

      if (!state || !state.postcode || !state.suburb) {
        return;
      }

      var estimator = document.querySelector(settings.estimatorSelector);

      if (!estimator) {
        return;
      }

      var postcode = String(state.postcode).trim();
      var suburb = String(state.suburb).trim();
      var key = postcode + '|' + suburb.toUpperCase();
      var now = Date.now();

      if (!force && lastEstimate.key === key && now - lastEstimate.time < 1000) {
        return;
      }

      var country = estimator.querySelector('[name="country"]');
      var province = estimator.querySelector('[name="province"]');
      var zip = estimator.querySelector('[name="zip"]');
      var city = estimator.querySelector('[name="city"]');
      var estimateButton = estimator.querySelector('[data-action="estimate-shipping"]');

      if (!country || !province || !zip || !city || !estimateButton) {
        return;
      }

      if (selectOption(country, state.country || 'Australia')) {
        country.dispatchEvent(new Event('change', { bubbles: true }));
      }

      selectOption(province, state.province || provinceForPostcode(postcode));
      province.dispatchEvent(new Event('change', { bubbles: true }));

      zip.value = postcode;
      city.value = suburb;
      zip.dispatchEvent(new Event('input', { bubbles: true }));
      city.dispatchEvent(new Event('input', { bubbles: true }));

      lastEstimate.key = key;
      lastEstimate.time = now;
      setEstimating(true);
      estimateButton.click();
    }

    function scheduleEstimate(state, force, delay) {
      clearTimeout(estimateTimer);
      estimateTimer = setTimeout(function () {
        estimate(state, force);
      }, delay || 0);
    }

    // Drops a quote that no longer describes the destination or the line item
    // it was asked for. Showing a stale price is worse than showing none.
    function invalidate() {
      clearTimeout(estimateTimer);
      lastEstimate.key = '';
      lastEstimate.time = 0;
      setEstimating(false);

      var estimator = document.querySelector(settings.estimatorSelector);
      var results = estimator && estimator.querySelector('.shipping-estimator__results');

      if (results) {
        results.innerHTML = '';
        // ShippingEstimator pins display:block inline, which would keep the
        // now-empty block taking up space.
        results.style.removeProperty('display');
      }
    }

    document.addEventListener('delivery-location:changed', function (event) {
      if (settings.auto) {
        scheduleEstimate(event.detail && event.detail.state, true);
      } else {
        invalidate();
      }
    });

    document.addEventListener('click', function (event) {
      var refresh = event.target.closest('[data-action="refresh-shipping"]');

      if (!refresh || !refresh.closest(settings.scopeSelector)) {
        return;
      }

      scheduleEstimate(null, true);
    });

    document.addEventListener('theme:loading:end', function () {
      setEstimating(false);
    });

    if (settings.bind) {
      settings.bind({ schedule: scheduleEstimate, invalidate: invalidate });
    }

    if (settings.auto) {
      scheduleEstimate(null, false);
    }
  }

  function initCartShippingEstimate() {
    if (!window.theme || window.theme.pageType !== 'cart') {
      return;
    }

    initShippingEstimate({
      auto: true,
      scopeSelector: '[data-section-type="cart"]',
      estimatorSelector: '[data-section-type="cart"] .shipping-estimator',
      cardSelector: '[data-section-type="cart"] .cart-delivery-card',
      bind: function (api) {
        document.documentElement.addEventListener('cart:rerendered', function () {
          api.schedule(null, true);
        });
      }
    });
  }

  function initProductShippingEstimate() {
    if (!window.theme || window.theme.pageType !== 'product') {
      return;
    }

    initShippingEstimate({
      // Quoting a product page costs a call to the carrier for a line item the
      // customer has not committed to, so it waits for the Estimate button.
      auto: false,
      scopeSelector: '[data-section-type="product"]',
      estimatorSelector: '[data-section-type="product"] .shipping-estimator',
      cardSelector: '[data-section-type="product"] .product-delivery-card',
      bind: function (api) {
        // The rate is quoted for whatever the add-to-cart form holds, so a
        // different variant or quantity leaves the shown price describing
        // something the customer is no longer looking at.
        document.addEventListener('variant:change', function () {
          api.invalidate();
        });

        document.addEventListener('change', function (event) {
          var input = event.target.closest && event.target.closest('[name="quantity"]');

          if (!input || !input.closest('[data-section-type="product"]')) {
            return;
          }

          api.invalidate();
        });
      }
    });
  }

  function initCartDeliveryProgress() {
    function renderBarLabel(label, text) {
      var match = /^(\u{1F389})\s*(.*)$/u.exec(text);
      var nodes = [];

      if (match) {
        var emoji = document.createElement('span');
        emoji.className = 'cart-delivery-progress__emoji';
        emoji.setAttribute('aria-hidden', 'true');
        emoji.textContent = match[1];
        nodes.push(emoji);
        text = match[2];
      }

      var copy = document.createElement('span');
      copy.className = 'cart-delivery-progress__copy';
      copy.textContent = text;
      nodes.push(copy);
      label.replaceChildren.apply(label, nodes);
    }

    function formatMoney(amountInCents) {
      var currency = window.Shopify && Shopify.currency && Shopify.currency.active
        ? Shopify.currency.active
        : 'AUD';
      var locale = window.Shopify && Shopify.locale ? Shopify.locale : 'en-AU';

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(amountInCents / 100);
    }

    function updateOne(progress, state) {
      var cartTotal = parseInt(progress.getAttribute('data-cart-total'), 10) || 0;
      var thresholds = [];

      if (state && state.eligible && state.threshold) {
        thresholds.push(Math.round(parseFloat(state.threshold) * 100));
      }

      // A cart of "$200+ AU Wide" items ships free anywhere in Australia, so it
      // keeps a target even with no destination saved. Where both apply the
      // customer reaches the cheaper one first.
      var auWideThreshold = parseInt(progress.getAttribute('data-au-wide-threshold'), 10) || 0;

      if (auWideThreshold > 0) {
        thresholds.push(auWideThreshold);
      }

      thresholds = thresholds.filter(function (value) {
        return value > 0;
      });

      if (!thresholds.length) {
        progress.hidden = true;
        return;
      }

      var threshold = Math.min.apply(null, thresholds);
      var unlocked = cartTotal >= threshold;
      var remaining = Math.max(threshold - cartTotal, 0);
      var percentage = Math.min(cartTotal / threshold * 100, 100);
      var track = progress.querySelector('[data-delivery-progress-track]');
      var label = progress.querySelector('[data-delivery-progress-label]');
      var barText = unlocked
        ? progress.getAttribute('data-bar-unlocked')
        : progress.getAttribute('data-bar-remaining').replace('__AMOUNT__', formatMoney(remaining));

      progress.hidden = false;
      progress.classList.toggle('cart-delivery-progress--unlocked', unlocked);
      track.style.setProperty('--delivery-progress', percentage + '%');
      renderBarLabel(label, barText);

      track.setAttribute('aria-valuemax', String(threshold / 100));
      track.setAttribute('aria-valuenow', String(Math.min(cartTotal, threshold) / 100));
      track.setAttribute('aria-valuetext', barText);
    }

    function update(state) {
      var nodes = document.querySelectorAll('[data-cart-delivery-progress]');

      if (!nodes.length) {
        return;
      }

      state = state || config.readState();

      for (var i = 0; i < nodes.length; i++) {
        updateOne(nodes[i], state);
      }
    }

    document.addEventListener('delivery-location:changed', function (event) {
      update(event.detail && event.detail.state);
    });

    // Mini-cart rerenders dispatch `cart:rerendered` on the header without
    // bubbles, so this has to listen in the capture phase.
    document.addEventListener('cart:rerendered', function () {
      update(config.readState());
    }, true);

    update(config.readState());
  }

  function formatOfferAmount(cents) {
    var value = Number(cents) / 100;

    if (!isFinite(value)) {
      return '';
    }

    return Math.round(value) === value ? '$' + value : '$' + value.toFixed(2);
  }

  function buildOfferSlides(state, copy) {
    var rules = state && Array.isArray(state.rules) ? state.rules.slice() : [];

    rules = rules.filter(function (rule) {
      return isFinite(Number(rule.min_subtotal)) && isFinite(Number(rule.fee));
    }).sort(function (a, b) {
      return Number(a.min_subtotal) - Number(b.min_subtotal);
    });

    if (!rules.length) {
      return copy.auspost ? [copy.auspost] : [];
    }

    var slides = [];
    var floor = Number(rules[0].min_subtotal);

    if (floor > 0 && copy.under) {
      slides.push(copy.under.replace('__AMOUNT__', formatOfferAmount(floor)));
    }

    rules.forEach(function (rule) {
      var amount = formatOfferAmount(rule.min_subtotal);

      if (Number(rule.fee) === 0) {
        slides.push(copy.free.replace('__AMOUNT__', amount));
      } else {
        slides.push(
          copy.fee
            .replace('__AMOUNT__', amount)
            .replace('__FEE__', formatOfferAmount(rule.fee))
        );
      }
    });

    return slides.filter(Boolean);
  }

  function initOfferTicker() {
    var ticker = document.querySelector('[data-delivery-offer-ticker]');
    var track = document.querySelector('[data-delivery-offer-track]');
    var offer = ticker && ticker.closest('.delivery-location-offer');

    if (!ticker || !track || !offer) {
      return;
    }

    var copy = {
      auspost: ticker.getAttribute('data-copy-auspost') || '',
      under: ticker.getAttribute('data-copy-under') || '',
      fee: ticker.getAttribute('data-copy-fee') || '',
      free: ticker.getAttribute('data-copy-free') || ''
    };
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var index = 0;
    var slideCount = 0;
    var timer = null;
    var paused = false;

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    function advance() {
      if (paused || slideCount < 2) {
        return;
      }

      index += 1;
      track.style.setProperty('--delivery-offer-shift', String(index));

      if (index < slideCount) {
        return;
      }

      track.addEventListener('transitionend', function reset(event) {
        if (event.propertyName && event.propertyName !== 'transform') {
          return;
        }

        track.removeEventListener('transitionend', reset);
        track.style.transition = 'none';
        index = 0;
        track.style.setProperty('--delivery-offer-shift', '0');
        track.offsetHeight;
        track.style.transition = '';
      });
    }

    function start() {
      stop();

      if (slideCount > 1 && !reduceMotion) {
        timer = setInterval(advance, 3500);
      }
    }

    function render(state) {
      stop();
      index = 0;
      track.style.transition = 'none';
      track.style.setProperty('--delivery-offer-shift', '0');
      track.replaceChildren();

      if (!state || !state.postcode || !state.suburb) {
        offer.classList.remove('is-ticking');
        ticker.hidden = true;
        ticker.removeAttribute('aria-label');
        return;
      }

      var slides = buildOfferSlides(state, copy);
      var conditions = offer.querySelector('.delivery-location-offer__conditions');

      slides.forEach(function (text) {
        var slide = document.createElement('span');
        var label = document.createElement('span');

        slide.className = 'delivery-location-offer__slide';
        label.textContent = text;
        slide.appendChild(label);

        if (conditions) {
          slide.appendChild(conditions.cloneNode(true));
        }

        track.appendChild(slide);
      });

      slideCount = slides.length;

      if (slideCount > 1 && !reduceMotion) {
        var clone = track.firstChild.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }

      ticker.hidden = false;
      offer.classList.add('is-ticking');
      ticker.setAttribute(
        'aria-label',
        slides.join('. ') + (conditions ? '. ' + (conditions.getAttribute('aria-label') || '') : '')
      );
      track.offsetHeight;
      track.style.transition = '';
      start();
    }

    offer.addEventListener('mouseenter', function () {
      paused = true;
    });

    offer.addEventListener('mouseleave', function () {
      paused = false;
    });

    document.addEventListener('delivery-location:changed', function (event) {
      render(event.detail && event.detail.state);
    });

    render(config.readState());
  }

  function pinTopbar() {
    var topbar = document.querySelector('.delivery-location-topbar');

    if (!topbar) {
      return;
    }

    document.documentElement.style.setProperty(
      '--delivery-location-topbar-height',
      topbar.offsetHeight + 'px'
    );
  }

  initDrawer();
  initCartShippingEstimate();
  initProductShippingEstimate();
  initCartDeliveryProgress();
  initOfferTicker();
  refreshStoredZone();
  pinTopbar();
  window.addEventListener('resize', pinTopbar);
})();
